import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Textarea,
  ButtonSecondary,
  ButtonSize,
  IconArrowRight,
  IconAnimatedDots,
  PermissionDenied,
  IconLoader,
} from '@ui/src';
import { NotificationEntityType } from '../../hoc/center/types';
import Comment from './comment';
import { CommentColumnTypes } from './types';

import cn from './styles.module.css';

const CommentColumn = ({
  notification,
  comment,
  setComment,
  commentsListAvailable,
  commentAvailable,
  editCommentsAvailable,
  onSendComment,
  requestInProgress,
  commentsIsLoading,
  commentError,
  comments,
  notificationEntityIsLoading,
  notificationEntity,
  transactionTypes,
  updateIssueCommentAction,
  updateIssueCommentInProgress,
  updateIssueCommentIsSuccess,
}: CommentColumnTypes) => {
  const [notificationEntityLink, setNotificationEntityLink] = useState('');
  const [notificationEntityLinkName, setNotificationEntityLinkName] =
    useState('');

  const { pathname } = useLocation();

  const categoryTitle = notification?.category
    .split('_')
    .map((item) => `${item.charAt(0)}${item.slice(1).toLowerCase()}`)
    .join(' ')
    ?.replace('Issues', 'Tasks');
  const groupTitle = notification?.group
    .split('_')
    .map((item) => `${item.charAt(0)}${item.slice(1).toLowerCase()}`)
    .join(' ')
    ?.replace('Issues', 'Tasks');
  const actionTitle = notification?.type
    .split('_')
    .map((item) => `${item.charAt(0)}${item.slice(1).toLowerCase()}`)
    .join(' ')
    ?.replace('Issues', 'Tasks');

  useEffect(() => {
    if (notification || notificationEntity) {
      let link = '';
      let linkName = '';

      const linkNameDescription = `${notification?.type
        ?.replace(`${notification?.group}_`, '')
        ?.split('_')
        ?.map((item) => `${item.charAt(0)}${item.slice(1).toLowerCase()}`)
        .join(' ')}`;

      if (notification?.entityType === NotificationEntityType.APPLICATION) {
        link = '/customers/new-application';

        if (notificationEntity?.type === 'INDIVIDUAL') {
          link = `${link}/individual/${notificationEntity?.id}`;
          linkName = `${notificationEntity?.firstName} ${notificationEntity?.lastName}: ${linkNameDescription}`;
        } else if (notificationEntity?.type === 'CORPORATE') {
          link = `${link}/corporate/${notificationEntity?.id}`;
          linkName = `${notificationEntity?.companyName}: ${linkNameDescription}`;
        } else if (notificationEntity?.type === 'COMPANY_OFFICIAL') {
          link = `${link}/company-official/${notificationEntity?.id}`;
          linkName = `${notificationEntity?.firstName} ${notificationEntity?.lastName}: ${linkNameDescription}`;
        }
      } else if (notification?.entityType === NotificationEntityType.CHANNEL) {
        link = '/messages';

        if (notificationEntity?.application) {
          if (notificationEntity?.application?.firstName) {
            linkName = `${notificationEntity?.application?.firstName} ${notificationEntity?.application?.lastName}: `;
          } else if (notificationEntity?.application?.companyName) {
            linkName = `${notificationEntity?.application?.companyName}: `;
          }
        } else if (notificationEntity?.organization) {
          if (notificationEntity?.organization?.firstName) {
            linkName = `${notificationEntity?.organization?.firstName} ${notificationEntity?.organization?.lastName}: `;
          } else if (notificationEntity?.organization?.companyName) {
            linkName = `${notificationEntity?.organization?.companyName}: `;
          }
        }

        linkName = `${linkName}${linkNameDescription}`;

        if (notificationEntity?.channelType === 'MAIN') {
          link = `${link}/main-chat/all/${notificationEntity?.id}`;
        } else {
          link = `${link}/requests/all/${notificationEntity?.id}`;
        }
      } else if (
        notification?.entityType === NotificationEntityType.OFFICIALS ||
        notification?.entityType === NotificationEntityType.WARNING_GROUP ||
        notification?.entityType === NotificationEntityType.ORGANIZATION ||
        notification?.entityType ===
          NotificationEntityType.CLIENT_SCORE_RESULT ||
        notification?.entityType === NotificationEntityType.AGREEMENT ||
        notification?.entityType === NotificationEntityType.DOCUMENT
      ) {
        link = '/customers';

        if (notification?.organization || notificationEntity) {
          const type =
            notification?.organization?.type || notificationEntity?.type;
          const firstName =
            notification?.organization?.firstName ||
            notificationEntity?.firstName;
          const lastName =
            notification?.organization?.lastName ||
            notificationEntity?.lastName;
          const companyName =
            notification?.organization?.companyName ||
            notificationEntity?.companyName;
          const id = notification?.organization?.id || notificationEntity?.id;

          if (type === 'INDIVIDUAL' || type === 'COMPANY_OFFICIAL') {
            link = `${link}/individual`;
            linkName = `${firstName} ${lastName}: `;

            if (
              notification?.entityType ===
                NotificationEntityType.WARNING_GROUP ||
              notification.entityType ===
                NotificationEntityType.CLIENT_SCORE_RESULT
            ) {
              link = `${link}/${id}/aml/transactions`;
            } else if (
              notification.entityType === NotificationEntityType.ORGANIZATION
            ) {
              link = `${link}/${id}`;
            } else if (
              notification.entityType === NotificationEntityType.AGREEMENT ||
              notification.entityType === NotificationEntityType.DOCUMENT
            ) {
              link = `${link}/${id}/documents`;
            }
          } else if (type === 'CORPORATE') {
            link = `${link}/corporate`;
            linkName = `${companyName}: `;

            if (notification?.entityType === NotificationEntityType.OFFICIALS) {
              link = `${link}/${id}/overview/transactions/company-official`;
            }

            if (
              notification?.entityType ===
                NotificationEntityType.WARNING_GROUP ||
              notification.entityType ===
                NotificationEntityType.CLIENT_SCORE_RESULT
            ) {
              link = `${link}/${id}/aml/transactions`;
            } else if (
              notification.entityType === NotificationEntityType.ORGANIZATION
            ) {
              link = `${link}/${id}`;
            } else if (
              notification.entityType === NotificationEntityType.AGREEMENT ||
              notification.entityType === NotificationEntityType.DOCUMENT
            ) {
              link = `${link}/${id}/documents`;
            }
          }
        }

        linkName = `${linkName}${linkNameDescription}`;
      } else if (
        notification?.entityType === NotificationEntityType.TRANSACTION &&
        transactionTypes?.length
      ) {
        link = '/transactions';

        const currentTrancationType = transactionTypes?.find(
          (item) => item.id === notificationEntity?.typeId,
        );

        if (currentTrancationType?.name === 'CURRENCY_EXCHANGE') {
          link = `${link}/exchange`;
          linkName = 'Exchange: ';
        } else if (currentTrancationType?.name === 'OUTGOING') {
          link = `${link}/outgoing`;
          linkName = 'Outgoing: ';
        } else if (currentTrancationType?.name === 'INCOMING') {
          link = `${link}/incoming`;
          linkName = 'Incoming: ';
        } else if (currentTrancationType?.name === 'NOSTRO') {
          link = `${link}/nostro`;
          linkName = 'Nostro: ';
        } else if (currentTrancationType?.name === 'INTERNAL') {
          link = `${link}/internal`;
          linkName = 'Internal: ';
        }

        link = `${link}/${notificationEntity?.id}`;
        linkName = `${linkName}${linkNameDescription}`;
      } else if (
        notification?.entityType === NotificationEntityType.PAYMENT_ORDER
      ) {
        link = `/transactions/cash-collection/${notificationEntity?.id}`;
        linkName = `Cash Collection: ${linkNameDescription}`;
      } else if (
        notification?.entityType === NotificationEntityType.NOTIFICATION
      ) {
        const notificationTypeArray = notificationEntity?.type?.split('_');
        link = `${pathname}?id=${notificationEntity?.id}`;

        if (notificationTypeArray) {
          linkName = `Notification: ${notificationTypeArray[0].charAt(
            0,
          )}${notificationTypeArray[0]
            .slice(1)
            .toLowerCase()} - ${notificationTypeArray
            .slice(1)
            .map(
              (typeValue: string) =>
                `${typeValue.charAt(0)}${typeValue.slice(1).toLowerCase()}`,
            )
            .join(' ')}`;
        }
      } else if (notification?.entityType === NotificationEntityType.LOCK) {
        link = `/administration/security/locks/${notification?.entityId}`;
        linkName = `Lock: ${linkNameDescription}`;
      }

      setNotificationEntityLink(link);
      setNotificationEntityLinkName(linkName);
    } else if (notificationEntityLink && !notificationEntity) {
      setNotificationEntityLink('');
      setNotificationEntityLinkName('');
    }
  }, [notification, notificationEntity]);

  let content;

  if (commentsListAvailable) {
    content = (
      <div>
        {notification && (
          <>
            <div className={cn.notificationCommentColumnBreadcrumbs}>
              {`${categoryTitle?.charAt(0)}${categoryTitle
                ?.slice(1)
                .toLowerCase()} / ${groupTitle}`}
            </div>
            <div className={cn.notificationCommentColumnTitle}>
              <span>
                {`${categoryTitle?.charAt(0)}${categoryTitle
                  ?.slice(1)
                  .toLowerCase()}`}
              </span>
              &nbsp;- {actionTitle?.replace(categoryTitle as string, '')}
            </div>
            {notification?.organization && (
              <div className={cn.notificationCommentColumnOrganization}>
                {`Customer: ${
                  notification?.organization?.firstName
                    ? `${notification?.organization?.firstName} ${notification?.organization?.lastName}`
                    : notification?.organization?.companyName
                }`}
              </div>
            )}
            {notificationEntityIsLoading && (
              <IconAnimatedDots className={cn.entityLinkLoader} />
            )}
            {!notificationEntityIsLoading && notificationEntityLink && (
              <NavLink
                to={notificationEntityLink}
                className={cn.notificationLink}
                target="_blank"
              >
                <IconArrowRight width={14} height={14} />
                <span>{notificationEntityLinkName}</span>
              </NavLink>
            )}

            {commentAvailable && (
              <>
                <Textarea
                  name="comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Enter comment..."
                />
                {commentError && (
                  <div className={cn.commentError}>{commentError}</div>
                )}
                <div className={cn.sendComment}>
                  <ButtonSecondary
                    className="button-white"
                    text={requestInProgress ? 'Sending...' : 'Send'}
                    onClick={() => onSendComment()}
                    size={ButtonSize.SMALL}
                  />
                </div>
              </>
            )}
            {commentsIsLoading && (
              <div className={cn.commentsLoader}>
                <IconLoader />
              </div>
            )}
            {comments && comments?.length > 0 && !commentsIsLoading && (
              <div className={cn.commentsWrapper}>
                {comments?.map((item) => (
                  <Comment
                    key={item.id}
                    comment={item}
                    editAvailable={editCommentsAvailable}
                    updateIssueCommentAction={updateIssueCommentAction}
                    updateIssueCommentInProgress={updateIssueCommentInProgress}
                    updateIssueCommentIsSuccess={updateIssueCommentIsSuccess}
                  />
                ))}
              </div>
            )}
            {!comments?.length && !commentsIsLoading && (
              <div className={cn.emptyComments}>No comments yet</div>
            )}
          </>
        )}
      </div>
    );
  } else {
    content = <PermissionDenied />;
  }

  return (
    <div
      className={`${cn.notificationCenterColumn} ${cn.notificationCenterCommentColumn}`}
    >
      {content}
    </div>
  );
};

export default CommentColumn;
